import React from 'react';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { MultiPartInput } from 'Views/Components/New/CompositeInputs';
import { PriceCellKind, PriceCellState, priceCellOptions } from 'Global/Pricing/PriceCellState';
import { UserPriceRow, readRowCell } from './userPricingRows';

const PRICE_CELL_SELECT_OPTIONS: Array<{ value: PriceCellKind; label: string }> = (
	Object.keys(priceCellOptions) as PriceCellKind[]
).map(value => ({ value, label: priceCellOptions[value] }));

const priceAdornment = (kind: PriceCellKind): string | null => {
	if (kind === 'PERCENTAGE') return '%';
	if (kind === 'FIXED') return '$';
	return null;
};

export interface UserPriceCellProps {
	/** The row occupying this cell's slot — possibly a virtual placeholder not yet in form state. */
	row: UserPriceRow;
	/** Persist a cell state; the host upserts the row into form state (see upsertPriceRow). */
	onWrite: (next: PriceCellState) => void;
	isReadOnly?: boolean;
	error?: boolean;
	/** Optional clamp for the percentage upper bound. Defaults to 100. */
	percentageMax?: number;
	/** Read-only label for an INHERIT cell. Defaults to 'Standard'. */
	inheritLabel?: string;
	/** Optional stable hook for e2e flows, wrapped around the editable cell. */
	testId?: string;
}

/**
 * The account page's fork of the shared PriceCell: same (mode, amount) coupling behind one
 * MultiPartInput, but form-state-driven — the row prop is the source of truth and every write
 * flows out through onWrite as a PriceCellState. The blank-Fixed-vs-Standard ambiguity is
 * carried on the row itself (pendingFixed, set by applyCellToRow), replacing both the shared
 * cell's local pendingKind state and the MobX pendingFixedRegistry. Local valueText remains the
 * controlled-input string mirror that lets partial decimals ("0.") survive reconciliation.
 */
export function UserPriceCell({
	row,
	onWrite,
	isReadOnly = false,
	error = false,
	percentageMax = 100,
	inheritLabel = 'Standard',
	testId,
}: UserPriceCellProps) {
	const modelState = readRowCell(row);

	// (amount=null, mode=FIXED) is ambiguous: "Standard" or "Fixed, no amount typed yet". The
	// row's pendingFixed flag records the explicit Fixed choice, so resolve INHERIT to FIXED
	// while it's set.
	const effectiveKind: PriceCellKind = modelState.kind === 'INHERIT' && row.pendingFixed ? 'FIXED' : modelState.kind;

	const [valueText, setValueText] = React.useState<string>('');

	// When the row amount changes from outside (sync with default, bulk apply, programmatic
	// clear), keep the mirror in step unless the user is mid-typing a partial decimal that
	// matches the row value.
	const modelAsString = row.amount == null ? '' : String(row.amount);
	const matchesModel = (() => {
		if (valueText === '' || valueText === '-') return row.amount == null;
		const parsed = Number(valueText);
		return Number.isFinite(parsed) && parsed === row.amount;
	})();
	const valueForInput = matchesModel ? valueText || modelAsString : modelAsString;

	const handleModeChange = (nextKind: PriceCellKind) => {
		if (nextKind === effectiveKind) return;
		if (nextKind === 'INHERIT') {
			onWrite({ kind: 'INHERIT' });
		} else if (nextKind === 'PERCENTAGE') {
			onWrite({ kind: 'PERCENTAGE', amount: undefined });
		} else {
			// Blank Fixed — applyCellToRow marks the row pendingFixed so the selector holds.
			onWrite({ kind: 'FIXED', amount: undefined });
		}
		setValueText('');
	};

	const handleValueChange = (next: string) => {
		setValueText(next);
		if (next === '' || next === '-') {
			// Empty/sign-only input → leave the amount cleared. Don't change kind — the
			// selector still shows FIXED (via pendingFixed) or PERCENTAGE so the user can
			// resume typing without re-picking the mode.
			const kind = effectiveKind === 'INHERIT' ? 'FIXED' : effectiveKind;
			onWrite(
				kind === 'PERCENTAGE'
					? { kind: 'PERCENTAGE', amount: undefined }
					: { kind: 'FIXED', amount: undefined },
			);
			return;
		}
		const parsed = Number(next);
		if (!Number.isFinite(parsed)) return;
		// Typing into an INHERIT cell auto-promotes it to FIXED (no other kind makes sense for
		// the very first keystroke — Percentage is reached via the selector).
		const targetKind = effectiveKind === 'INHERIT' ? 'FIXED' : effectiveKind;
		onWrite({ kind: targetKind, amount: parsed } as PriceCellState);
	};

	if (isReadOnly) {
		switch (effectiveKind) {
			case 'INHERIT':
				return <span className="text-gray-500 italic">{inheritLabel}</span>;
			case 'FIXED':
				return <span>{formatCurrency(row.amount ?? 0)}</span>;
			case 'PERCENTAGE':
				return <span>{`${row.amount ?? 0}% off`}</span>;
			default:
				return null;
		}
	}

	const isPercentage = effectiveKind === 'PERCENTAGE';
	const isInherit = effectiveKind === 'INHERIT';

	return (
		<div data-test-id={testId}>
			<MultiPartInput
				label=""
				parts={[
					{
						key: 'mode',
						type: 'select',
						width: 'w-36',
						props: {
							value: PRICE_CELL_SELECT_OPTIONS.find(o => o.value === effectiveKind),
							options: PRICE_CELL_SELECT_OPTIONS,
							onChange: (opt: { value: PriceCellKind } | null) => {
								if (opt) handleModeChange(opt.value);
							},
							isDisabled: isReadOnly,
							isClearable: false,
						},
					},
					{
						key: 'value',
						type: 'text',
						props: {
							type: 'number',
							value: valueForInput,
							onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(e.target.value),
							onWheel: (e: React.WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur(),
							min: isPercentage ? 0 : undefined,
							max: isPercentage ? percentageMax : undefined,
							step: isPercentage ? 0.1 : '0.01',
							startAdornment: priceAdornment(effectiveKind),
							placeholder: isInherit ? '' : '0',
							disabled: isReadOnly || isInherit,
							error: error ? 'Invalid price' : undefined,
						},
					},
				]}
			/>
		</div>
	);
}
